import { createI18n } from 'vue-i18n'
import en from './locales/en.json'
import fr from './locales/fr.json'
import es from './locales/es.json'
import it from './locales/it.json'

// i18n configuration constants
export const LANGUAGE_STORAGE_KEY = 'vte-language'
export const defaultLanguage = 'en'
export const fallbackLanguage = 'en'
export const availableLanguages = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', dbId: 1 },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', dbId: 3 },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', dbId: 4 },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', dbId: 5 },
] as const

export type SupportedLocale = 'en' | 'fr' | 'es' | 'it'

/**
 * Get the initial locale based on priority:
 * 1. localStorage saved preference
 * 2. Browser language
 * 3. Default language
 */
function getInitialLocale(): SupportedLocale {
  // Check localStorage
  const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY)
  if (saved && isLocaleSupported(saved)) {
    return saved as SupportedLocale
  }

  // Check browser language
  const browserLang = navigator.language.split('-')[0]
  if (isLocaleSupported(browserLang)) {
    return browserLang as SupportedLocale
  }

  return defaultLanguage
}

/**
 * Check if a locale is supported
 */
export function isLocaleSupported(locale: string): boolean {
  return availableLanguages.some(lang => lang.code === locale)
}

/**
 * Create the i18n instance
 * Note: Locale is set lazily to avoid temporal dead zone issues
 */
export const i18n = createI18n({
  legacy: false, // Use Composition API mode
  locale: 'en', // Default locale, will be updated on init
  fallbackLocale: fallbackLanguage,
  messages: {
    en,
    fr,
    es,
    it
  }
})

/**
 * Initialize the locale based on localStorage or browser preference
 * Call this after the app is mounted to avoid circular dependency issues
 */
export function initializeLocale(): void {
  const locale = getInitialLocale()
  i18n.global.locale.value = locale
  document.documentElement.setAttribute('lang', locale)
}

/**
 * Set the current locale and persist to localStorage
 */
export function setLocale(locale: SupportedLocale) {
  if (!isLocaleSupported(locale)) {
    console.warn(`Locale "${locale}" is not supported, falling back to "${defaultLanguage}"`)
    locale = defaultLanguage
  }

  i18n.global.locale.value = locale
  localStorage.setItem(LANGUAGE_STORAGE_KEY, locale)
  document.documentElement.setAttribute('lang', locale)
}

/**
 * Get the current locale
 */
export function getLocale(): SupportedLocale {
  return i18n.global.locale.value as SupportedLocale
}

/**
 * Initialize locale from user profile (called after auth)
 */
export function initLocaleFromUser(languageId: number | null | undefined) {
  if (!languageId) return

  const language = availableLanguages.find(lang => lang.dbId === languageId)
  if (language) {
    setLocale(language.code as SupportedLocale)
  }
}
